import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { api, Comment } from '../utils/api';
import { useAuthStore } from '../stores/auth';
import { tooltips } from '../config/tooltips';

interface CommentsProps {
  articleId: string;
  articleAuthorId: string;
}

export function Comments({ articleId, articleAuthorId }: CommentsProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorEmail, setAuthorEmail] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { user, isAuthenticated } = useAuthStore();

  const fetchComments = async () => {
    try {
      const { comments } = await api.getComments(articleId);
      setComments(comments);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [articleId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await api.createComment({
        article_id: articleId,
        content: newComment,
        author_name: isAuthenticated ? undefined : authorName || 'Anonymous',
        author_email: isAuthenticated ? undefined : authorEmail || undefined,
        parent_id: replyTo || undefined,
      });

      setNewComment('');
      setAuthorName('');
      setAuthorEmail('');
      setReplyTo(null);
      await fetchComments();
    } catch (error) {
      console.error('Failed to create comment:', error);
      alert('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;

    try {
      await api.deleteComment(commentId);
      await fetchComments();
    } catch (error) {
      console.error('Failed to delete comment:', error);
      alert('Failed to delete comment');
    }
  };

  const canDelete = (comment: Comment) => {
    if (!isAuthenticated || !user) return false;
    return (
      comment.user_id === user.id ||
      articleAuthorId === user.id ||
      user.role === 'admin'
    );
  };

  const renderComment = (comment: Comment, depth = 0) => (
    <div
      key={comment.id}
      className={`${depth > 0 ? 'ml-8 border-l-2 border-gray-100 dark:border-gray-700 pl-4' : ''}`}
    >
      <div className="py-4">
        <div className="flex items-start gap-3">
          {comment.user_avatar ? (
            <img
              src={comment.user_avatar}
              alt={comment.user_name || comment.author_name}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                {(comment.user_name || comment.author_name).charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="comment-author font-medium text-gray-900">
                {comment.user_name || comment.author_name}
              </span>
              <time className="comment-time text-sm text-gray-500">
                {formatDistanceToNow(new Date(comment.created_at), {
                  addSuffix: true,
                })}
              </time>
            </div>
            <p className="comment-content text-gray-700 mt-1 whitespace-pre-wrap">{comment.content}</p>
            <div className="flex items-center gap-4 mt-2">
              <button
                onClick={() => setReplyTo(comment.id)}
                className="comment-action text-sm text-gray-500 hover:text-gray-700"
              >
                Reply
              </button>
              {canDelete(comment) && (
                <button
                  onClick={() => handleDelete(comment.id)}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {comment.replies?.map((reply) => renderComment(reply, depth + 1))}
    </div>
  );

  return (
    <div className="mt-12">
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        Comments ({comments.length})
      </h3>

      {/* Comment form */}
      <form onSubmit={handleSubmit} className="mb-8">
        {replyTo && (
          <div className="flex items-center gap-2 mb-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Replying to comment</span>
            <button
              type="button"
              onClick={() => setReplyTo(null)}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Cancel
            </button>
          </div>
        )}
        {!isAuthenticated && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              placeholder="Name (optional)"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              className="input"
            />
            <input
              type="email"
              placeholder="Email (optional)"
              value={authorEmail}
              onChange={(e) => setAuthorEmail(e.target.value)}
              className="input"
            />
          </div>
        )}
        <textarea
          placeholder="Write a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          rows={3}
          className="input resize-none"
        />
        <div className="flex justify-end mt-2">
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            className="btn-primary p-2"
            title={tooltips.postComment}
            aria-label={tooltips.postComment}
          >
            {submitting ? (
              <div className="spinner w-5 h-5" />
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
      </form>

      {/* Comments list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="spinner" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          No comments yet. Be the first to comment!
        </p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {comments.map((comment) => renderComment(comment))}
        </div>
      )}
    </div>
  );
}
